// NEW_FILE_JUSTIFIED: Extracts all data logic from MyCoveragePage into a reusable hook — reduces page to thin orchestrator
import { useQuery } from 'convex/react';
import { useLocation } from 'react-router-dom';
import { useMemo, useCallback } from 'react';
import { api } from '../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: hooks in src/hooks/
import type { Id } from '../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: need Convex Id type
import type { ICoveragePageData, IUtilizationData, IBenefitSectionData } from '@/@types/coverage';
import type { IBenefitPlanSnapshotData } from '@/@types/claims';
import { generateCertificatePdf } from '@/lib/certificates/generateCertificate';

function snapshotToSections(snapshot: IBenefitPlanSnapshotData): IBenefitSectionData[] {
  return snapshot.sections.map((s, idx) => ({
    _id: `snapshot-section-${idx}` as Id<'benefitSections'>, // AS_JUSTIFIED: synthetic ID for snapshot sections — components need Id shape but snapshot data has no real IDs
    name: s.name,
    sortOrder: s.sortOrder,
    items: s.items.map((item, itemIdx) => ({
      _id: (item.originalItemId || `snapshot-item-${idx}-${itemIdx}`) as Id<'benefitItems'>, // AS_JUSTIFIED: use originalItemId for utilization mapping
      sectionId: `snapshot-section-${idx}` as Id<'benefitSections'>, // AS_JUSTIFIED: synthetic section ID for snapshot items
      planId: '' as Id<'benefitPlans'>, // AS_JUSTIFIED: not used by display components
      orgId: '' as Id<'organisations'>, // AS_JUSTIFIED: not used by display components
      name: item.name,
      description: item.description,
      planPaysPercent: item.planPaysPercent,
      youPayPercent: item.youPayPercent,
      annualLimit: item.annualLimit,
      lifetimeLimit: item.lifetimeLimit,
      visitLimit: item.visitLimit,
      waitingPeriodMonths: item.waitingPeriodMonths,
      cashBenefitPerUnit: item.cashBenefitPerUnit,
      cashBenefitUnit: item.cashBenefitUnit,
      cashBenefitMaxUnits: item.cashBenefitMaxUnits,
      limitType: item.limitType,
      limitValue: item.limitValue,
      visitOrDayCap: item.visitOrDayCap,
      preAuthRequired: item.preAuthRequired,
      coverageConditions: item.coverageConditions,
      benefitSpecificNotes: item.benefitSpecificNotes,
      sortOrder: item.sortOrder,
      createdAt: 0,
      updatedAt: 0,
    })),
  }));
}

export function useCoveragePage(policyId: string | undefined): ICoveragePageData {
  const { pathname } = useLocation();
  const basePath = pathname.startsWith('/company') ? '/company/my-insurance' : '/my-insurance';

  const typedPolicyId = policyId as Id<'insurancePolicies'> | undefined; // AS_JUSTIFIED: policyId from route params — one-time cast at hook top

  const assignments = useQuery(
    api.insurance.benefitAssignments.getByPolicyForPortal,
    typedPolicyId ? { policyId: typedPolicyId } : 'skip'
  );

  const utilization = useQuery(
    api.insurance.benefitAssignments.getUtilizationForPortal,
    typedPolicyId ? { policyId: typedPolicyId } : 'skip'
  );

  const firstAssignment = assignments?.[0];
  const hasSnapshot = !!firstAssignment?.snapshotData;

  // Only fetch live plan if no snapshot exists (legacy policies)
  const plan = useQuery(
    api.insurance.benefitPlans.getForPortal,
    firstAssignment && typedPolicyId && !hasSnapshot
      ? { planId: firstAssignment.planId, policyId: typedPolicyId }
      : 'skip'
  );

  const certificateData = useQuery(
    api.insurance.certificateData.getCertificateData,
    typedPolicyId ? { policyId: typedPolicyId } : 'skip'
  );

  // Build utilization map — use originalItemId from snapshot for mapping
  const utilizationByItemId = useMemo(() => {
    const map = new Map<string, IUtilizationData>();
    if (utilization) {
      for (const u of utilization) {
        map.set(u.benefitItemId, { amountUsed: u.amountUsed, visitsUsed: u.visitsUsed });
      }
    }
    return map;
  }, [utilization]);

  const handleDownloadCertificate = useCallback(async () => {
    if (!certificateData) return;
    const blob = await generateCertificatePdf(certificateData);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `certificate-${certificateData.policyholder?.policyNumber ?? 'policy'}.pdf`;
    link.click();
    URL.revokeObjectURL(url);
  }, [certificateData]);

  // Build plan data from snapshot or live plan
  const planData = useMemo(() => {
    if (hasSnapshot && firstAssignment?.snapshotData) {
      const snapshot = firstAssignment.snapshotData as IBenefitPlanSnapshotData; // AS_JUSTIFIED: snapshotData is v.any() from Convex, typed as IBenefitPlanSnapshotData
      return {
        name: snapshot.plan.name,
        status: snapshot.plan.status,
        currency: snapshot.plan.currency,
        maxAnnualBenefit: snapshot.plan.maxAnnualBenefit,
        annualDeductible: snapshot.plan.annualDeductible,
        coinsurancePlanPays: snapshot.plan.coinsurancePlanPays,
        coinsuranceYouPay: snapshot.plan.coinsuranceYouPay,
        areaOfCoverage: snapshot.plan.areaOfCoverage,
        sections: snapshotToSections(snapshot),
      };
    }

    if (plan) {
      return {
        name: plan.name,
        status: plan.status,
        currency: plan.currency,
        maxAnnualBenefit: plan.maxAnnualBenefit,
        annualDeductible: plan.annualDeductible,
        coinsurancePlanPays: plan.coinsurancePlanPays,
        coinsuranceYouPay: plan.coinsuranceYouPay,
        areaOfCoverage: plan.areaOfCoverage,
        sections: plan.sections.map((s: (typeof plan.sections)[number]) => ({
          _id: s._id,
          name: s.name,
          sortOrder: s.sortOrder,
          items: s.items,
        })),
      };
    }

    return null;
  }, [hasSnapshot, firstAssignment, plan]);

  const isLoading =
    assignments === undefined ||
    utilization === undefined ||
    (!hasSnapshot && firstAssignment !== undefined && plan === undefined);

  const hasNoPlan = !isLoading && (!planData || assignments.length === 0);

  return {
    basePath,
    plan: planData,
    planName: firstAssignment?.planName,
    utilizationByItemId,
    handleDownloadCertificate,
    canDownload: !!certificateData,
    isLoading,
    hasNoPlan,
  };
}
